import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { setDoc, doc, deleteDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { collection, query, where, addDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import fundoImage from "../assets/fundo.webp";
import Hero from "../components/Hero";
import PageLayout from "../components/PageLayout";
import Footer from "../components/Footer";
import FormContainer from "../components/FormContainer";
import Seo from "../components/Seo";
import { safeGetDoc, safeGetDocs } from "../lib/safeGet";
import useLanguageConfig from "../lib/useLanguageConfig";
import { allRateKeys, allAudioKeys, allSwornRateKeys } from "../lib/languageConfig";

const RESOLVE_ACCOUNT_URL =
  "https://us-central1-alpha-translations.cloudfunctions.net/resolveAccount";
const ENSURE_CLIENT_ROLE_URL =
  "https://us-central1-alpha-translations.cloudfunctions.net/ensureClientRole";

const Register = () => {
  const { t } = useTranslation();
  const languageCfg = useLanguageConfig();
  React.useEffect(() => {
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "";
    };
  }, []);

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [erro, setErro] = useState("");
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [inviteAccepted, setInviteAccepted] = useState(false);
  const [inviterInfo, setInviterInfo] = useState(null);
  const navigate = useNavigate();
  const [emailError] = useState("");
  const [telefone, setTelefone] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  // Conta já existente (detectada por resolveAccount) + ativação de cliente.
  const [existingAccount, setExistingAccount] = useState(null);
  const [loginPassword, setLoginPassword] = useState("");
  const [activating, setActivating] = useState(false);

  // Bloquear scroll da página quando o modal estiver aberto
  React.useEffect(() => {
    if (step === 2 && emailCheckResult?.userType === "colab") {
      // Adicionar classe para bloquear scroll da página
      document.body.classList.add("modal-open");
    } else {
      // Remover classe para restaurar scroll da página
      document.body.classList.remove("modal-open");
    }

    // Cleanup quando o componente for desmontado
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [step, emailCheckResult?.userType]);

  const handleNext = async () => {
    if (!email.trim()) {
      setErro(t("auth.emailRequired"));
      return;
    }
    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email)) {
      setErro(t("auth.invalidEmail"));
      return;
    }
    try {
      // Detecta de forma confiável se o e-mail JÁ tem identidade no Auth
      // (cliente, afiliado, colaborador...). Cobre contas que não têm doc em
      // `users` (ex.: afiliado), evitando o beco "email-already-in-use".
      try {
        const resp = await fetch(RESOLVE_ACCOUNT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const acc = await resp.json().catch(() => ({}));
        if (resp.ok && acc.exists) {
          setExistingAccount(acc);
          setLoginPassword("");
          setErro("");
          setStep(4); // Tela: você já tem conta → entrar para ativar cliente
          return;
        }
      } catch (resolveErr) {
        console.error("Erro ao resolver conta:", resolveErr);
        // Em caso de falha do resolve, segue o fluxo normal (o createUser ainda
        // protege contra duplicidade).
      }

      const q = query(
        collection(db, "emailcheck"),
        where("email", "==", email)
      );
      const querySnapshot = await safeGetDocs(q, "emailcheck");
      if (!querySnapshot.empty) {
        const docData = querySnapshot.docs[0].data();
        const registeredBy = docData.registeredBy || null;
        if (!registeredBy) {
          throw new Error(
            "O campo 'registeredBy' está ausente ou vazio na coleção 'emailcheck'."
          );
        }

        // Buscar informações do convidador na coleção users
        const inviterQuery = query(
          collection(db, "users"),
          where("email", "==", registeredBy)
        );
        const inviterSnapshot = await safeGetDocs(inviterQuery, "inviter");
        let inviterData = null;
        if (!inviterSnapshot.empty) {
          inviterData = inviterSnapshot.docs[0].data();
        }
        setEmailCheckResult({ userType: "colab", registeredBy });
        setInviterInfo(
          inviterData
            ? {
                nomeCompleto: inviterData.nomeCompleto || registeredBy,
                ...inviterData,
              }
            : { nomeCompleto: registeredBy }
        );
        setStep(2); // Vai para o modal de convite
      } else {
        setEmailCheckResult({ userType: "b2c", registeredBy: null });
        setInviterInfo(null);
        setStep(3); // Vai direto para o registro como cliente
        setErro(t("auth.emailNotFoundInColab"));
      }
      setErro("");
    } catch (error) {
      // O detalhe técnico fica no console. Para quem está se cadastrando, a
      // mensagem crua do SDK (ex.: "IndexedDbTransactionError… Allocate target
      // failed") só assusta e faz abandonar o cadastro — o safeGetDocs já
      // tentou de novo e já contornou o cache local antes de chegar aqui.
      console.error("Erro na consulta à coleção emailcheck:", error);
      setErro(t("auth.emailCheckFailed"));
    }
  };

  const handleAcceptInvite = () => {
    setInviteAccepted(true);
    setStep(3); // Vai para o registro como colaborador
  };

  const handleDeclineInvite = () => {
    setInviteAccepted(false);
    setEmailCheckResult({ userType: "b2c", registeredBy: null });
    setInviterInfo(null);
    setStep(3); // Vai para o registro como cliente
  };

  // Conta já existe: a pessoa entra com a senha (prova de dono) e o servidor
  // anexa o papel de cliente à conta existente (ensureClientRole).
  const handleActivateClient = async () => {
    if (activating) return;
    if (!loginPassword) {
      setErro(t("auth.passwordRequired", "Informe sua senha."));
      return;
    }
    setActivating(true);
    setErro("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email, loginPassword);
      const token = await cred.user.getIdToken();
      const resp = await fetch(ENSURE_CLIENT_ROLE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nome: nomeCompleto || undefined,
          telefone: telefone || undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.error || t("auth.registerError"));
      }
      // Recarrega a sessão com o novo papel e entra no app de cliente.
      window.location.href = "/client/projects";
    } catch (err) {
      console.error("Erro ao ativar acesso de cliente:", err);
      const code = err?.code || "";
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setErro(t("auth.invalidCredentials"));
      } else if (code === "auth/too-many-requests") {
        setErro(t("auth.tooManyRequests"));
      } else {
        setErro(err.message || t("auth.loginError"));
      }
      setActivating(false);
    }
  };

  const handleRegister = async () => {
    if (isRegistering) return;
    setIsRegistering(true);
    if (senha !== confirmarSenha) {
      setErro(t("auth.passwordMismatch"));
      setIsRegistering(false);
      return;
    }
    if (!nomeCompleto.trim() || !email.trim() || !senha || !telefone.trim()) {
      setErro(t("auth.fillAllFields"));
      setIsRegistering(false);
      return;
    }
    if (senha.length < 6) {
      setErro(t("auth.passwordMinLength"));
      setIsRegistering(false);
      return;
    }
    if (!emailCheckResult) {
      setErro(t("auth.emailValidationError"));
      setIsRegistering(false);
      return;
    }

    let userType = "b2c";
    // Tarifas: um campo por par ativo do catálogo de idiomas (incl. juramentada). O colaborador
    // herda as do cliente que o convidou; o cliente novo, as globais.
    const rateFields = [
      ...allRateKeys(languageCfg),
      ...allAudioKeys(languageCfg),
      ...allSwornRateKeys(languageCfg),
    ];
    const copyRates = (source) =>
      Object.fromEntries(rateFields.map((k) => [k, source?.[k] ?? 0]));
    let rates = copyRates(null);
    let registeredByType = null;
    let canTest = false;

    try {
      // Firebase autentica automaticamente após createUserWithEmailAndPassword
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        senha
      );
      const user = userCredential.user;

      if (emailCheckResult.userType === "colab" && inviteAccepted) {
        // Agora que o usuário está autenticado, podemos buscar informações do convidador
        const q = query(
          collection(db, "users"),
          where("email", "==", emailCheckResult.registeredBy)
        );
        const querySnapshot = await safeGetDocs(q, "registeredBy");
        if (!querySnapshot.empty) {
          const registeredByDoc = querySnapshot.docs[0].data();
          userType = "colab";
          rates = copyRates(registeredByDoc);
          registeredByType = registeredByDoc.userType;
          canTest = registeredByDoc.canTest || false;

          const emailCheckQuery = query(
            collection(db, "emailcheck"),
            where("email", "==", email)
          );
          const emailCheckSnapshot = await safeGetDocs(emailCheckQuery, "emailcheck");
          if (!emailCheckSnapshot.empty) {
            const emailCheckDoc = emailCheckSnapshot.docs[0];
            const emailCheckData = emailCheckDoc.data();
            const logData = {
              timestamp: new Date(),
              userEmail: emailCheckResult.registeredBy,
              action: "convite de colaborador",
              details: {
                emailConvidado: email,
                status: "aceito",
                colaborador: {
                  nome: nomeCompleto || email.split("@")[0],
                  email: email,
                  tipo: userType === "b2b" ? "B2B" : "B2C",
                  status: "Ativo",
                  canTest: canTest,
                },
                dataConvite:
                  emailCheckData.createdAt?.toDate().toLocaleString("pt-BR") ||
                  new Date().toLocaleString("pt-BR"),
                convidadoPor: emailCheckResult.registeredBy,
              },
            };
            await addDoc(collection(db, "activity_logs"), logData);
            await deleteDoc(emailCheckDoc.ref);
          }
        } else {
          throw new Error("Usuário registrador não encontrado");
        }
      } else {
        const priceDocRef = doc(db, "priceperpage", "price");
        const priceDocSnap = await safeGetDoc(priceDocRef, "priceperpage");
        if (priceDocSnap.exists()) {
          rates = copyRates(priceDocSnap.data());
        }
      }

      await setDoc(doc(db, "users", user.uid), {
        nomeCompleto,
        email,
        telefone, // garantir que telefone sempre é salvo
        userType,
        registeredBy: emailCheckResult.registeredBy,
        registeredByType,
        createdAt: new Date(),
        ...rates,
        canTest:
          emailCheckResult.userType === "colab" && inviteAccepted
            ? canTest
            : false,
      });

      // Disparar e-mail de boas-vindas
      try {
        await fetch(
          "https://us-central1-alpha-translations.cloudfunctions.net/sendUserRegisteredEmail",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ nome: nomeCompleto, nomeCompleto, email }),
          }
        );
      } catch (emailErr) {
        console.error("Erro ao enviar e-mail de boas-vindas:", emailErr);
        // Não impede o registro
      }

      if (emailCheckResult.userType === "colab" && inviteAccepted) {
        const emailCheckQuery = query(
          collection(db, "emailcheck"),
          where("email", "==", email)
        );
        const emailCheckSnapshot = await safeGetDocs(emailCheckQuery, "emailcheck");
        if (!emailCheckSnapshot.empty) {
          await deleteDoc(emailCheckSnapshot.docs[0].ref);
        }
      }

      alert(t("auth.registerSuccess"));

      // Adicionado: Fazer logout antes de navegar para o login
      await signOut(auth);
      console.log("Usuário deslogado após registro.");

      setStep(1);
      setNomeCompleto("");
      setEmail("");
      setSenha("");
      setConfirmarSenha("");
      setErro("");
      setInviteAccepted(false);
      setInviterInfo(null);
      setIsRegistering(false);
      navigate("/login");
    } catch (error) {
      console.error("Erro ao registrar usuário:", error);
      // Se o erro for na criação do usuário, ele não estará logado.
      // Se o erro for após a criação (ex: no setDoc ou signOut), o usuário pode ter sido criado.
      // O signOut aqui é uma tentativa de garantir o estado deslogado em caso de erro parcial.
      try {
        await signOut(auth);
        console.log("Tentativa de logout após erro no registro.");
      } catch (signOutError) {
        console.error(
          "Erro ao tentar deslogar após erro no registro:",
          signOutError
        );
      }
      const code = error?.code || "";
      let mensagem = t("auth.registerError");
      switch (code) {
        case "auth/email-already-in-use":
          mensagem = t("auth.emailAlreadyExists");
          break;
        case "auth/weak-password":
          mensagem = t("auth.passwordMinLength");
          break;
        case "auth/invalid-email":
          mensagem = t("auth.invalidEmail");
          break;
        case "auth/operation-not-allowed":
          mensagem = t("auth.registerDisabled");
          break;
        case "auth/network-request-failed":
          mensagem = t("auth.networkError");
          break;
        case "auth/too-many-requests":
          mensagem = t("auth.tooManyRequests");
          break;
        default:
          if (typeof error?.message === "string" && error.message) {
            mensagem = t("auth.registerError") + ": " + error.message;
          }
      }
      setErro(mensagem);
      setIsRegistering(false);
    }
  };

  return (
    <>
      <Seo
        title="Criar conta"
        description="Crie sua conta na Alpha Translations e solicite na hora um orçamento de tradução juramentada, certificada ou simples em Português, Espanhol e Inglês — para Brasil, América Latina e EUA."
        path="/register"
      />
      {/* Hero Section - Fora do PageLayout */}
      <div className="w-full overflow-hidden" style={{ width: "100%", maxWidth: "100vw" }}>
        <Hero
          backgroundImage={fundoImage}
          title={t("home.heroTitle")}
          height="h-[300px] md:h-[400px]"
          overlay="bg-black/40"
        />
      </div>

      {/* Conteúdo Principal */}
      <div className="relative -mt-[225px] z-10 bg-transparent">
        <PageLayout transparent>
          <div className="font-poppins text-gray-800 min-h-screen flex flex-col bg-transparent">
            <main className="flex-grow">
              <FormContainer
                title={t("auth.registerTitle")}
                error={erro}
                footerText={t("auth.hasAccount")}
                footerLinkText={t("common.login")}
                onFooterClick={() => navigate("/login")}
              >
                {step === 1 && (
                  <div className="h-[200px] flex flex-col justify-between p-3">
                    <h3 className="text-xl font-semibold text-center mb-4">
                      {t("auth.enterEmail")}
                    </h3>
                    <form
                      onSubmit={(e) => e.preventDefault()}
                      className="flex flex-col flex-1"
                    >
                      <div className="mb-5">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) =>
                            setEmail(
                              e.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9@._-]/g, "")
                            )
                          }
                          required
                          placeholder={t("auth.email")}
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base transition-colors"
                        />
                        {emailError && (
                          <p className="text-red-500 text-sm mt-1">
                            {emailError}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleNext}
                        className="mt-auto w-full bg-blue-600 text-white py-2 px-4 rounded-xl hover:bg-blue-700 transition-colors duration-200 text-base"
                      >
                        {t("common.next")}
                      </button>
                    </form>
                  </div>
                )}

                {step === 2 && emailCheckResult?.userType === "colab" && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="w-[500px] max-w-[90vw] max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                        <h3 className="text-lg font-medium text-gray-900">
                          {t("auth.collaboratorInvite")}
                        </h3>
                        <button
                          onClick={() => {
                            setStep(1);
                            setEmail("");
                            setErro("");
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Content - Scrollable Area */}
                      <div className="flex-1 overflow-y-auto p-6 modal-content">
                        <div className="text-center">
                          <div className="mb-6">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg
                                className="w-8 h-8 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">
                              {t("auth.inviteFound")}
                            </h4>
                            <p className="text-gray-600 mb-6">
                              {t("auth.invitedBy")}{" "}
                              <strong className="text-blue-600">
                                {inviterInfo?.nomeCompleto ||
                                  emailCheckResult.registeredBy}
                              </strong>{" "}
                              {t("auth.toBeCollaborator")}
                            </p>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <h5 className="text-sm font-medium text-blue-900 mb-3">
                              {t("auth.whatDoesThisMean")}
                            </h5>
                            <div className="text-sm text-blue-800 space-y-2">
                              <div className="flex items-start gap-2">
                                <svg
                                  className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                <span>{t("auth.willHaveAccess")}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <svg
                                  className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                <span>{t("auth.canManageProjects")}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <svg
                                  className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                <span>{t("auth.willKeepSettings")}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex justify-center gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
                        <button
                          onClick={handleDeclineInvite}
                          className="h-10 px-6 rounded-xl text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                        >
                          {t("auth.decline")}
                        </button>
                        <button
                          onClick={handleAcceptInvite}
                          className="h-10 px-6 rounded-xl text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {t("auth.accept")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="min-h-[420px] md:min-h-[350px]">
                    <h3 className="text-xl font-semibold text-center mb-6">
                      {t("auth.additionalInfo")}
                      {emailCheckResult?.userType === "colab" &&
                        inviteAccepted && (
                          <div className="text-sm text-green-600 mt-2">
                            ({t("auth.registeringAsCollaborator")})
                          </div>
                        )}
                    </h3>
                    <form
                      onSubmit={(e) => e.preventDefault()}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <label className="block text-sm font-medium">
                          {t("auth.fullName")}:
                        </label>
                        <input
                          type="text"
                          value={nomeCompleto}
                          onChange={(e) => setNomeCompleto(e.target.value)}
                          required
                          className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium">
                          {t("auth.phone")} (Whatsapp):
                        </label>
                        <input
                          type="tel"
                          value={telefone}
                          onChange={(e) => setTelefone(e.target.value)}
                          required
                          placeholder="+55 (99) 99999-9999"
                          className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium">
                          {t("auth.password")}:
                        </label>
                        <input
                          type="password"
                          value={senha}
                          onChange={(e) => setSenha(e.target.value)}
                          required
                          className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium">
                          {t("auth.confirmPassword")}:
                        </label>
                        <input
                          type="password"
                          value={confirmarSenha}
                          onChange={(e) => setConfirmarSenha(e.target.value)}
                          required
                          className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleRegister}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-xl hover:bg-blue-700 transition-colors duration-200"
                        disabled={isRegistering}
                      >
                        {isRegistering
                          ? t("auth.registering")
                          : t("auth.register")}
                      </button>
                    </form>
                  </div>
                )}
                {step === 4 && existingAccount && (() => {
                  const CLIENT_ROLES = ["b2b", "b2c", "colab"];
                  const roles = existingAccount.roles || [];
                  const alreadyClient = roles.some((r) =>
                    CLIENT_ROLES.includes(String(r).toLowerCase())
                  );
                  const noPassword = existingAccount.hasPasswordProvider === false;
                  return (
                    <div className="min-h-[260px] flex flex-col p-3">
                      <h3 className="text-xl font-semibold text-center mb-2">
                        {t("auth.accountExistsTitle", "Você já tem uma conta")}
                      </h3>
                      <p className="text-sm text-gray-600 text-center mb-5">
                        {alreadyClient
                          ? t(
                              "auth.accountExistsClient",
                              "Este e-mail já tem acesso de cliente. Faça login para continuar."
                            )
                          : noPassword
                          ? t(
                              "auth.accountNoPassword",
                              "Sua conta foi criada mas a senha ainda não foi definida. Use \"Esqueci minha senha\" na tela de login."
                            )
                          : t(
                              "auth.accountExistsActivate",
                              "Entre com sua senha para ativar o acesso de cliente nesta mesma conta."
                            )}
                      </p>

                      {alreadyClient || noPassword ? (
                        <button
                          type="button"
                          onClick={() => navigate("/login")}
                          className="mt-auto w-full bg-blue-600 text-white py-2 px-4 rounded-xl hover:bg-blue-700 transition-colors duration-200"
                        >
                          {t("common.login")}
                        </button>
                      ) : (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleActivateClient();
                          }}
                          className="flex flex-col gap-3"
                        >
                          <input
                            type="email"
                            value={email}
                            disabled
                            className="w-full p-2 border border-gray-200 bg-gray-50 rounded-xl text-gray-500"
                          />
                          <input
                            type="password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            required
                            placeholder={t("auth.password")}
                            className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          />
                          <button
                            type="submit"
                            disabled={activating}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-xl hover:bg-blue-700 transition-colors duration-200 disabled:opacity-60"
                          >
                            {activating
                              ? t("auth.loggingIn", "Entrando...")
                              : t("auth.activateClientAccess", "Entrar e ativar acesso de cliente")}
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="w-full text-blue-600 hover:text-blue-800 text-sm underline"
                          >
                            {t("auth.goToLogin", "Ir para o login")}
                          </button>
                        </form>
                      )}
                    </div>
                  );
                })()}
              </FormContainer>
            </main>
          </div>
        </PageLayout>
      </div>
      <Footer />
    </>
  );
};

export default Register;

// Estilos CSS para o modal
const modalStyles = `
  /* Estilos específicos para o modal no mobile */
  @media (max-width: 768px) {
    .modal-content {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: #cbd5e0 #f7fafc;
    }
    
    /* Garantir que o scroll funcione no modal */
    .modal-content {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      touch-action: pan-y;
    }
    
    /* Prevenir scroll da página quando modal está aberto */
    body.modal-open {
      overflow: hidden;
      position: fixed;
      width: 100%;
    }
  }

  /* Estilos gerais do modal */
  .modal-content {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
  }

  .modal-content::-webkit-scrollbar {
    width: 6px;
  }

  .modal-content::-webkit-scrollbar-track {
    background: #f7fafc;
  }

  .modal-content::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 3px;
  }

  .modal-content::-webkit-scrollbar-thumb:hover {
    background-color: #a0aec0;
  }
`;

// Adicionar estilos ao head do documento
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = modalStyles;
  document.head.appendChild(styleElement);
}
